import * as Joi from 'joi';
import { Column, Entity, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { GenPartial } from 'typeormgen';
import { Episodic } from '../..';
import ChangeLook from '../change-looks/change-look.entity';
import Character from '../characters/character.entity';
import { DEPARTMENTS } from '../enums/enums';
import EpisodicCharacter from '../episodic-characters/episodic-character.entity';
import EpisodicHaChange from '../episodic-ha-changes/episodic-ha-change.entity';
import HaChangeScene from '../ha-change-scenes/ha-change-scene.entity';
import HaLookPicture from '../ha-look-pictures/ha-look-picture.entity';
import Production from '../productions/production.entity';

@Entity('ha_changes')
export default class HaChange extends ChangeLook {
	static tableName = 'ha_changes';

	@Column('int')
	episodic_ha_change_id: number;

	@Column('text')
	h_description: string;

	@Column('varchar')
	wig_hp: string;

	@Column('varchar')
	wig_hp_source: string;

	@Column('varchar')
	h_period: string;

	@Column('varchar')
	h_products: string;

	@Column('varchar')
	h_note: string;

	@Column('varchar')
	hair_ornaments: string;

	@Column('varchar')
	hair_color_formula: string;

	@Column('varchar')
	maintenance: string;

	@Column('varchar')
	tools: string;

	@OneToMany((type) => HaLookPicture, (haLookPicture) => haLookPicture.haChange)
	haLookPictures: HaLookPicture[];

	@OneToMany((type) => HaChangeScene, (haChangeScene) => haChangeScene.haChange)
	haChangeScenes: HaChangeScene[];

	@ManyToOne((type) => Character, (character) => character.haChanges)
	@JoinColumn({
		name: 'char_id',
	})
	character: Character;

	@ManyToOne((type) => Production, (production) => production.haChanges)
	@JoinColumn({
		name: 'prod_id',
	})
	production: Production;

	@ManyToOne(
		(type) => EpisodicHaChange,
		(episodicHaChange) => episodicHaChange.haChanges
	)
	@JoinColumn({
		name: 'episodic_ha_change_id',
	})
	episodicHaChange: EpisodicHaChange;

	scene_ids: number[];

	static rules() {
		return {
			...super.rules(),
			episodic_ha_change_id: Joi.number().integer().allow(null),
			h_description: Joi.string().allow(null, ''),
			h_note: Joi.string().max(1000).allow(null, ''),
			h_period: Joi.string().max(1000).allow(null, ''),
			h_products: Joi.string().max(1000).allow(null, ''),
			hair_color_formula: Joi.string().max(300).allow(null, ''),
			hair_ornaments: Joi.string().max(1000).allow(null, ''),
			maintenance: Joi.string().max(300).allow(null, ''),
			scene_ids: Joi.array().items(Joi.number().integer()).allow(null),
			tools: Joi.string().max(300).allow(null, ''),
			wig_hp_source: Joi.string().max(1000).allow(null, ''),
			wig_hp: Joi.string().max(1000).allow(null, ''),
		};
	}

	toJSON() {
		return {
			...super.toJSON(),
			episodic_ha_change_id: this.episodic_ha_change_id,
			h_description: this.h_description,
			h_note: this.h_note,
			h_period: this.h_period,
			h_products: this.h_products,
			hair_color_formula: this.hair_color_formula,
			hair_ornaments: this.hair_ornaments,
			maintenance: this.maintenance,
			scene_ids: this.scene_ids ? this.scene_ids : null,
			tools: this.tools,
			wig_hp_source: this.wig_hp_source,
			wig_hp: this.wig_hp,
		};
	}

	assign(props?: GenPartial<HaChange>) {
		super.assign(props);

		if (props.scene_ids && typeof props.scene_ids === 'string') {
			// tslint:disable-next-line
			this.scene_ids = (<string>(<any>props.scene_ids))
				.split(',')
				.map((id) => Number(id));
		}
	}

	constructor(props?: GenPartial<HaChange>) {
		super(props);
	}

	get changeDescription() {
		return this.h_description;
	}

	set changeDescription(changeDescription: string) {
		this.h_description = changeDescription;
	}

	getDepartment() {
		return DEPARTMENTS.HA;
	}

	getTagName(data?: {
		production: Production;
		episodic: Episodic;
		characters: Character[];
		episodicCharacter: EpisodicCharacter;
	}) {
		if (!data) {
			return this.change_num;
		}
		const epCharDisplay = data.episodicCharacter.getFullName(data.characters);
		const { is_feature, shoot_method } = data.episodic;
		let episodeNum;
		// if traditional tv, show episode number
		if (!is_feature && !shoot_method && data.production) {
			episodeNum =
				data.production.episode_num && data.production.episode_num !== ''
					? data.production.episode_num
					: 'TBD';
			return `${epCharDisplay} - ${this.change_num} Look - Ep. ${episodeNum}`;
		}
		return `${epCharDisplay} - ${this.change_num} Look`;
	}
}
